/**
 * Lightweight frame accounting.
 *
 * Deliberately allocation-free and O(1) per frame: a diagnostics panel that itself causes
 * jank is worse than no diagnostics. Rates are exponential moving averages over ~1s so the
 * numbers are readable rather than twitchy.
 */

export class RateCounter {
  private lastTick = 0;

  private smoothedInterval = 0;

  private readonly smoothing: number;

  constructor(smoothing = 0.12) {
    this.smoothing = smoothing;
  }

  /** @returns the smoothed rate in Hz. */
  tick(nowMs: number): number {
    if (this.lastTick === 0) {
      this.lastTick = nowMs;
      return 0;
    }
    const interval = nowMs - this.lastTick;
    this.lastTick = nowMs;
    if (interval <= 0 || interval > 2000) return this.rate;
    this.smoothedInterval =
      this.smoothedInterval === 0
        ? interval
        : this.smoothedInterval + (interval - this.smoothedInterval) * this.smoothing;
    return this.rate;
  }

  get rate(): number {
    return this.smoothedInterval > 0 ? 1000 / this.smoothedInterval : 0;
  }

  /** Zero the rate when nothing has ticked for a while (e.g. tracking stopped). */
  decay(nowMs: number, idleMs = 2000): void {
    if (this.lastTick !== 0 && nowMs - this.lastTick > idleMs) {
      this.smoothedInterval = 0;
      this.lastTick = 0;
    }
  }

  reset(): void {
    this.lastTick = 0;
    this.smoothedInterval = 0;
  }
}

export class PerformanceMonitor {
  private readonly render = new RateCounter();

  private readonly inference = new RateCounter(0.2);

  private smoothedInferenceMs = 0;

  private dropped = 0;

  private lastRenderAt = 0;

  /** Call once per rendered frame. */
  onRender(nowMs: number): void {
    if (this.lastRenderAt !== 0) {
      const delta = nowMs - this.lastRenderAt;
      // Anything past ~2.5 display intervals at 60 Hz counts as a dropped frame.
      if (delta > 42) this.dropped += Math.min(10, Math.round(delta / 16.7) - 1);
    }
    this.lastRenderAt = nowMs;
    this.render.tick(nowMs);
  }

  /** Call once per inference result. */
  onInference(nowMs: number, inferenceMs: number): void {
    this.inference.tick(nowMs);
    this.smoothedInferenceMs =
      this.smoothedInferenceMs === 0
        ? inferenceMs
        : this.smoothedInferenceMs + (inferenceMs - this.smoothedInferenceMs) * 0.15;
  }

  /** Call once per rendered frame so stale rates fall to zero. */
  decay(nowMs: number): void {
    this.inference.decay(nowMs);
  }

  get renderFps(): number {
    return this.render.rate;
  }

  get inferenceFps(): number {
    return this.inference.rate;
  }

  get inferenceMs(): number {
    return this.smoothedInferenceMs;
  }

  get droppedFrames(): number {
    return this.dropped;
  }

  reset(): void {
    this.render.reset();
    this.inference.reset();
    this.smoothedInferenceMs = 0;
    this.dropped = 0;
    this.lastRenderAt = 0;
  }
}
